/**
 * Décision 372 — la demande de vérification d'une personne non éligible :
 * ce que l'accueil envoie, ce que la chargée lit et répond. Rien de médical.
 */
import { z } from 'zod';
import { TYPES_BENEFICIAIRE } from '../rules/beneficiaire.js';
import { ACTIONS_VERIFICATION, MOTIF_REFUS_VERIFICATION_MAX, NOTE_VERIFICATION_MAX, REPONSES_VERIFICATION, } from '../rules/verification-chargee.js';
export const STATUTS_VERIFICATION = ['OUVERTE', 'ACCEPTEE', 'REFUSEE'];
/** L'accueil demande : ce que dit la personne, obligatoire (décision 399 : plus de choix rapide). */
export const schemaDemandeVerification = z
    .object({
    note: z
        .string()
        .trim()
        .min(1, 'Écrivez ce que dit la personne.')
        .max(NOTE_VERIFICATION_MAX, `La note tient en ${String(NOTE_VERIFICATION_MAX)} caractères au plus.`),
})
    .strict();
/**
 * La chargée répond ; « Ne peut pas entrer » se motive (contrôlé par le
 * service). `action` : celle que la chargée a lue ;
 * si la situation a changé depuis, le serveur refuse (409) au lieu de faire
 * autre chose que ce que le bouton annonçait.
 */
export const schemaReponseVerification = z
    .object({
    reponse: z.enum(REPONSES_VERIFICATION),
    action: z.enum(ACTIONS_VERIFICATION).optional(),
    motif: z
        .string()
        .trim()
        .max(MOTIF_REFUS_VERIFICATION_MAX, `Le motif tient en ${String(MOTIF_REFUS_VERIFICATION_MAX)} caractères au plus.`)
        .optional(),
})
    .strict();
/** Une demande, telle que l'accueil la suit sur la fiche. */
export const schemaDemandeVerificationSuivie = z.object({
    id: z.uuid(),
    statut: z.enum(STATUTS_VERIFICATION),
    note: z.string(),
    raison: z.string(),
    demandeeLe: z.iso.datetime(),
    reponduLe: z.iso.datetime().nullable(),
    motifRefus: z.string().nullable(),
    /**
     * Décision 399 — ce que la réponse fait (ou a fait) : réactiver le dossier,
     * valider le salarié, prévenir seulement (déjà couvert), ou refuser seulement.
     */
    action: z.enum(ACTIONS_VERIFICATION),
});
/**
 * La fiche, vue du comptoir : la raison en une phrase, si la vérification
 * peut se demander, et la demande du jour à ce centre (la dernière).
 */
export const schemaVerificationDeLaFiche = z.object({
    raison: z.string().nullable(),
    possible: z.boolean(),
    demande: schemaDemandeVerificationSuivie.nullable(),
});
/** Une demande de l'accueil dans la ligne de la chargée : sur qui, où, et ce que dit la personne. */
export const schemaDemandeDeLaLigne = z.object({
    id: z.uuid(),
    personneId: z.uuid(),
    nom: z.string(),
    prenom: z.string().nullable(),
    typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
    /** Le centre où la personne attend. */
    site: z.string(),
    demandeeLe: z.iso.datetime(),
    demandeePar: z.string(),
    note: z.string(),
});
/**
 * Une ligne « Vérification » de la liste « À confirmer » de la chargée.
 * Décision 399 : une ligne par personne tranchée — le salarié, pour toutes
 * les demandes de sa famille refusées à cause de lui. `id` : la plus ancienne
 * de ces demandes, celle à laquelle on répond.
 */
export const schemaVerificationAConfirmer = z.object({
    id: z.uuid(),
    personneId: z.uuid(),
    nom: z.string(),
    prenom: z.string().nullable(),
    typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
    matricule: z.string().nullable(),
    entreprise: z.string().nullable(),
    /** Pour un ayant droit tranché pour lui-même (âge, retrait) : son salarié. */
    titulaire: z.object({ id: z.uuid(), nom: z.string(), prenom: z.string().nullable() }).nullable(),
    /** La plus ancienne demande : l'attente se compte depuis elle. */
    demandeeLe: z.iso.datetime(),
    /** « Ce que dit la plateforme » de la personne tranchée, lu à l'instant. */
    raison: z.string(),
    action: z.enum(ACTIONS_VERIFICATION),
    /** La phrase qui dit l'effet de la réponse. */
    effet: z.string(),
    /** Les demandes que la réponse tranche, la plus ancienne d'abord. */
    demandes: z.array(schemaDemandeDeLaLigne).min(1),
});
export const schemaVerificationsAConfirmer = z.object({
    elements: z.array(schemaVerificationAConfirmer),
});
//# sourceMappingURL=verification-chargee.js.map